import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, getOrganizationSchema } from '../../utils/seo';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';

export default function Impressum() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [showBackToTop, setShowBackToTop] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'Impressum – Seitenschmied Webdesign Gräfelfing München',
    description: 'Impressum und rechtliche Informationen von Seitenschmied. Angaben gemäß § 5 TMG. Blasio Hertz, Gstaller Weg 6, 82166 Gräfelfing.',
    canonical: `${siteUrl}/impressum`,
    noindex: true,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/impressum#webpage`,
          url: `${siteUrl}/impressum`,
          name: 'Impressum – Seitenschmied',
          description: 'Impressum und rechtliche Informationen von Seitenschmied gemäß § 5 TMG.',
          inLanguage: 'de-DE',
          publisher: { '@id': `${siteUrl}/#organization` },
          noIndex: true,
        },
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteNav navId="impressum-nav" />

      {/* Hero */}
      <div className="relative pt-24 sm:pt-32 pb-12 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full bg-teal-100 text-teal-700 font-bold text-sm mb-4 sm:mb-6">
            <i className="ri-file-list-3-line text-base sm:text-lg"></i>
            <span>Rechtliches</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Impressum
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Angaben gemäß § 5 TMG
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="bg-white rounded-2xl border-2 border-teal-200 shadow-lg p-6 sm:p-8 space-y-6 sm:space-y-8">
          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-line text-sm text-white"></i>
              </span>
              Angaben gemäß § 5 TMG
            </h2>
            <div className="space-y-2 text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              <p className="font-semibold text-gray-900">Seitenschmied</p>
              <p>Blasio Hertz</p>
              <p>Gstaller Weg 6</p>
              <p>82166 Gräfelfing</p>
              <p>Deutschland</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-sm text-white"></i>
              </span>
              Kontakt
            </h2>
            <div className="space-y-2 text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              <p>
                <strong className="text-gray-900">Telefon:</strong> +49 160 7676763
              </p>
              <p>
                <strong className="text-gray-900">E-Mail:</strong>{' '}
<a href="mailto:info@seitenschmied.com" className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300">
                  info@seitenschmied.com
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-sm text-white"></i>
              </span>
              Vertreten durch
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Blasio Hertz (Inhaber)
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-global-line text-sm text-white"></i>
              </span>
              EU-Streitschlichtung
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
              <a
                href="https://ec.europa.eu/consumers/odr"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300 break-all"
              >
                https://ec.europa.eu/consumers/odr
              </a>
              <br />
              Unsere E-Mail-Adresse finden Sie oben im Impressum.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-scales-line text-sm text-white"></i>
              </span>
              Verbraucherstreitbeilegung
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
              Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-sm text-white"></i>
              </span>
              Haftung für Inhalte
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den
              allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht
              verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu
              forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-link text-sm text-white"></i>
              </span>
              Haftung für Links
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben.
              Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der
              verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-copyright-line text-sm text-white"></i>
              </span>
              Urheberrecht
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen
              Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der
              Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="mt-8 sm:mt-12 text-center">
          <Link to="/termin-buchen">
            <button
              className="px-8 sm:px-12 py-4 sm:py-5 font-semibold text-base sm:text-lg rounded-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              style={{
                background: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
                color: '#FFFFFF',
                boxShadow: '0 8px 30px rgba(20, 184, 166, 0.3)',
              }}
            >
              Kostenloses Erstgespräch buchen
            </button>
          </Link>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-40 cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
            color: '#FFFFFF',
          }}
        >
          <i className="ri-arrow-up-line text-xl sm:text-2xl"></i>
        </button>
      )}

      <SiteFooter />
    </div>
  );
}
